import { useTranslation } from 'react-i18next';

interface SecurityItemProps {
  icon: string;
  title: string;
  description: string;
}

const SecurityItem = ({ icon, title, description }: SecurityItemProps) => {
  return (
    <div className="flex items-start space-x-4 group">
      <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-cyan-500/25 transition-all duration-300">
        <i className={`${icon} text-xl text-white`}></i>
      </div>
      <div>
        <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">
          {title}
        </h4>
        <p className="text-gray-300 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

const Security = () => {
  const { t } = useTranslation('common');

  const securityItems = [
    {
      icon: 'ri-shield-check-line',
      title: t('security.left.items.0.title'),
      description: t('security.left.items.0.description')
    },
    {
      icon: 'ri-key-line',
      title: t('security.left.items.1.title'),
      description: t('security.left.items.1.description')
    },
    {
      icon: 'ri-lock-line',
      title: t('security.left.items.2.title'),
      description: t('security.left.items.2.description')
    },
    {
      icon: 'ri-eye-line',
      title: t('security.left.items.3.title'),
      description: t('security.left.items.3.description')
    }
  ];

  return (
    <section id="security" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,rgba(6,182,212,0.1),transparent_120deg,rgba(139,92,246,0.1),transparent_240deg)]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            {t('security.title')}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Side - Security Measures */}
          <div className="space-y-6 lg:space-y-8">
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl border border-slate-700/50 backdrop-blur-sm p-6 sm:p-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-star-line text-xl text-white"></i>
                </div>
                {t('security.left.title')}
              </h3>
              
              <div className="space-y-4 sm:space-y-6">
                {securityItems.map((item, index) => (
                  <SecurityItem
                    key={index}
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Compliance */}
          <div className="space-y-6 lg:space-y-8">
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl border border-slate-700/50 backdrop-blur-sm p-6 sm:p-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-award-line text-xl text-white"></i>
                </div>
                {t('security.right.title')}
              </h3>

              <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8">
                {t('security.right.description')}
              </p>

              {/* Compliance Badges */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-4 sm:p-6 text-center">
                  <i className="ri-security-line text-3xl sm:text-4xl text-green-400 mb-2"></i>
                  <div className="text-xs sm:text-sm font-semibold text-green-400">ISO 27001</div>
                  <div className="text-xs text-gray-400">{t('security.right.iso')}</div>
                </div>
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-4 sm:p-6 text-center">
                  <i className="ri-shield-check-line text-3xl sm:text-4xl text-blue-400 mb-2"></i>
                  <div className="text-xs sm:text-sm font-semibold text-blue-400">SOC 2</div>
                  <div className="text-xs text-gray-400">{t('security.right.soc')}</div>
                </div>
              </div>

              {/* Policy Links */}
              <div className="space-y-2 sm:space-y-3">
                {[t('security.right.privacy'), t('security.right.terms')].map((link, index) => (
                  <button 
                    key={index}
                    className="w-full flex items-center justify-between p-3 sm:p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-cyan-500/30 hover:bg-slate-700/50 transition-all duration-300 group text-sm sm:text-base"
                  >
                    <span className="text-gray-300 group-hover:text-cyan-400 transition-colors">
                      {link}
                    </span>
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-cyan-400 transition-colors"></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Security Illustration */}
            <div className="relative hidden sm:block">
              <img 
                src="/images/security-illustration.svg"
                alt="Security Infrastructure"
                className="w-full h-64 sm:h-80 object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Security;